import { type ChangeEvent, useMemo, useRef, useState } from "react";

import { Button } from "@/components/Interface/Button/Button";
import { getHeader } from "@/components/Portal/Cockpit/DeviceManagement/Roaming/roamingUtils";
import {
  addBasketEntry,
  addContract,
  addOption,
  checkSIM,
  listAvailableOptions,
  removeBasketEntry,
  saveBasket,
  setEntryCustomData,
  setICCID,
  useGetCustomer,
} from "@/services/api";
import { Basket } from "@/services/model";

import {
  BASE_OPTION_PRODUCT_ID,
  type BulkSimRowState,
  getStepLabel,
  parseBulkSimCsv,
  ROW_DELAY_MS,
  setRowFailure,
  setRowStep,
  setRowSuccess,
  STEP_DELAY_MS,
  WAIT_POLL_INTERVAL_MS,
  WAIT_TIMEOUT_MS,
} from "./bulkSimActivationUtils";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const waitFor = async (
  predicate: () => boolean,
  timeoutMs = WAIT_TIMEOUT_MS,
  intervalMs = WAIT_POLL_INTERVAL_MS,
) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (predicate()) return;
    await sleep(intervalMs);
  }
  throw new Error("Timed out waiting for backend data");
};

const getFirstEntryUuid = (basket: Basket) => basket.entries["0"]?.uuid;

const getEntryIndexes = (basket: Basket) =>
  Object.keys(basket.entries)
    .map((index) => Number.parseInt(index, 10))
    .sort((a, b) => b - a);

const requireBasketId = (basket: Basket) => {
  if (!basket.id) {
    throw new Error("Basket id missing in backend response");
  }

  return basket.id;
};

export const BulkSimActivation = () => {
  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomer();
  const [rows, setRows] = useState<BulkSimRowState[]>([]);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const stopRequestedRef = useRef(false);

  const displayName = [customer?.firstName, customer?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const customerLabel = displayName || customer?.email || "Unknown customer";
  const paymentMode = customer?.paymentMethod?.paymentMode;
  const isManualPaymentMode = paymentMode === "Manual";

  const pendingRowsCount = useMemo(
    () => rows.filter((row) => row.status === "pending").length,
    [rows],
  );

  const updateRowAt = (
    index: number,
    updater: (row: BulkSimRowState) => BulkSimRowState,
  ) => {
    setRows((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? updater(row) : row)),
    );
  };

  const onCsvSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const parsedRows = parseBulkSimCsv(text);
    if (parsedRows.length === 0) {
      setCsvError(
        "CSV must include ICCID, PUK, and Alias columns (e.g. Inv | ICCID, PUK 1, Alias)",
      );
      setRows([]);

      return;
    }

    setCsvError(null);
    setRows(parsedRows);
  };

  const processRows = async () => {
    if (!rows.length || isProcessing || !isManualPaymentMode) {
      return;
    }

    stopRequestedRef.current = false;
    setIsProcessing(true);

    for (let index = 0; index < rows.length; index += 1) {
      if (stopRequestedRef.current) break;

      const snapshot = rows[index];
      if (!snapshot || snapshot.status !== "pending") {
        continue;
      }

      try {
        updateRowAt(index, (row) => setRowStep(row, "checkSim"));
        const simCheck = await checkSIM(snapshot.iccid);
        if (simCheck.message !== "Sim is available" || !simCheck.optionId) {
          throw new Error("SIM is not available or no optionId returned");
        }
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "saveBasket"));
        let basket = await saveBasket();
        let basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        const existingIndexes = getEntryIndexes(basket);
        for (const basketIndex of existingIndexes) {
          basket = await removeBasketEntry(basketId, basketIndex);
          basketId = requireBasketId(basket);
          await sleep(STEP_DELAY_MS);
        }

        updateRowAt(index, (row) => setRowStep(row, "addBasketEntry"));
        basket = await addBasketEntry(basketId, BASE_OPTION_PRODUCT_ID, {
          isESIM: false,
        });
        basketId = requireBasketId(basket);
        const entryUuid = getFirstEntryUuid(basket);
        await waitFor(() => Boolean(entryUuid));
        if (!entryUuid) {
          throw new Error(
            "No basket entry uuid available after addBasketEntry",
          );
        }
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "setICCID"));
        basket = await setICCID(basketId, 0, {
          iccid: snapshot.iccid,
          puk: snapshot.puk,
          clientId: 1,
        });
        basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "setAlias"));
        basket = await setEntryCustomData(basketId, entryUuid, {
          SimAlias: snapshot.alias,
        });
        basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "addOption"));
        const options = await listAvailableOptions(basketId, 0);
        const matchingOption = options.find(
          (option) => option.product?.id === simCheck.optionId,
        );
        if (!matchingOption) {
          throw new Error("No matching option found for SIM optionId");
        }
        if (!matchingOption.precharged) {
          throw new Error("Only precharged SIM options are allowed");
        }
        basket = await addOption(basketId, entryUuid, {
          optionId: simCheck.optionId,
          quantity: 1,
          isONP: false,
        });
        basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "checkout"));
        await addContract(
          {
            header: getHeader(),
            basketId,
          },
          { autoRenew: true },
        );

        updateRowAt(index, (row) => setRowSuccess(row));
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Activation failed";
        updateRowAt(index, (row) => setRowFailure(row, row.step, message));
      }

      await sleep(ROW_DELAY_MS);
    }

    setIsProcessing(false);
    stopRequestedRef.current = false;
  };

  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-2 text-2xl font-semibold text-primary-100">
        Bulk SIM Activation
      </h1>
      <p className="mb-4 text-secondary-100">
        Upload a CSV and run SIM activation sequentially with onboarding
        endpoints.
      </p>

      <div className="mb-6 rounded border border-secondary-75 p-4">
        <h2 className="text-lg font-semibold text-primary-100">
          Logged-in user
        </h2>
        {isLoadingCustomer ? (
          <p className="text-secondary-100">Loading customer...</p>
        ) : (
          <div className="mt-2 text-sm text-secondary-100">
            <p>
              <strong>Name:</strong> {customerLabel}
            </p>
            <p>
              <strong>Customer ID:</strong> {customer?.id ?? "n/a"}
            </p>
            <p>
              <strong>Email:</strong> {customer?.email ?? "n/a"}
            </p>
            <p>
              <strong>Payment mode:</strong> {paymentMode ?? "n/a"}
            </p>
          </div>
        )}
      </div>

      {!isManualPaymentMode && !isLoadingCustomer && (
        <div className="mb-4 rounded border border-error-100 p-3 text-error-100">
          Activation is restricted to customers with payment mode{" "}
          <strong>Manual</strong>.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={onCsvSelected}
          disabled={isProcessing}
        />
        <Button
          className="accent"
          disabled={!rows.length || isProcessing || !isManualPaymentMode}
          onClick={processRows}
        >
          Start activation ({pendingRowsCount} pending)
        </Button>
        <Button
          className="accent inverted"
          disabled={!isProcessing}
          onClick={() => {
            stopRequestedRef.current = true;
          }}
        >
          Stop after current row
        </Button>
      </div>

      {csvError && <p className="mb-4 text-error-100">{csvError}</p>}

      <div className="overflow-x-auto rounded border border-secondary-75">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-primary-5">
            <tr>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">ICCID</th>
              <th className="px-3 py-2">PUK</th>
              <th className="px-3 py-2">Alias</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.iccid}-${index}`}
                className="border-t border-secondary-75"
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{row.status}</div>
                  <div className="text-xs text-secondary-100">
                    {getStepLabel(row.step)}
                  </div>
                  {row.errorMessage && (
                    <div className="text-xs text-error-100">
                      {row.errorMessage}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">{row.iccid}</td>
                <td className="px-3 py-2">{row.puk}</td>
                <td className="px-3 py-2">{row.alias}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-secondary-100" colSpan={4}>
                  No CSV data loaded.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
